import { del, get, post, put } from '../request';

const configuredStaticBase = import.meta.env.VITE_STATIC_BASE?.replace(/\/$/, '') || '';
const configuredApiBase = import.meta.env.VITE_API_BASE?.replace(/\/$/, '') || '/backend-api';
const configuredVideoApiBase = import.meta.env.VITE_VIDEO_API_BASE?.replace(/\/$/, '') || '';

function upgradeToHttpsIfNeeded(url: string) {
  const value = String(url || '').trim();
  if (!value)
    return value;
  if (typeof window !== 'undefined' && window.location?.protocol === 'https:' && value.startsWith('http://'))
    return `https://${value.slice('http://'.length)}`;
  return value;
}

function joinBaseAndPath(base: string, path: string) {
  const b = String(base || '').replace(/\/$/, '');
  const p = String(path || '');
  if (!b)
    return p;
  if (!p)
    return b;
  if (p.startsWith('/'))
    return `${b}${p}`;
  return `${b}/${p}`;
}

function toQueryString(params?: Record<string, any>) {
  if (!params)
    return '';
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null)
      return;
    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry !== undefined && entry !== null)
          query.append(key, String(entry));
      });
      return;
    }
    query.append(key, String(value));
  });
  const text = query.toString();
  return text ? `?${text}` : '';
}

function getFallbackGetUrls(path: string, params?: Record<string, any>) {
  const urls = new Set<string>();
  const query = toQueryString(params);
  const normalizedPath = `${path}${query}`;

  urls.add(normalizedPath);
  urls.add(joinBaseAndPath(configuredApiBase, normalizedPath));

  if (configuredVideoApiBase)
    urls.add(joinBaseAndPath(configuredVideoApiBase, normalizedPath));

  if (typeof window !== 'undefined' && window.location?.origin) {
    const origin = window.location.origin;
    urls.add(joinBaseAndPath(origin, normalizedPath));
    urls.add(joinBaseAndPath(`${origin}/backend-api`, normalizedPath));
  }

  return Array.from(urls).filter(Boolean);
}

async function getWithFallback<T>(path: string, params?: Record<string, any>) {
  try {
    return await get<T>(path, params);
  }
  catch (primaryError) {
    const token = localStorage.getItem('token') || '';
    const urls = getFallbackGetUrls(path, params);
    let lastError: unknown = primaryError;

    for (const url of urls) {
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'include',
        });

        if (!response.ok)
          throw new Error(`HTTP ${response.status}`);

        const payload = await response.json();
        if (payload && typeof payload === 'object' && 'code' in payload)
          return payload;

        if (payload && typeof payload === 'object' && 'status' in payload && 'data' in payload) {
          return { code: 0, message: String(payload.status || 'success'), data: payload.data } as any;
        }

        return { code: 0, message: 'success', data: payload } as any;
      }
      catch (error) {
        lastError = error;
      }
    }

    throw lastError;
  }
}

function resolveApiOrigin() {
  try {
    if (/^https?:\/\//i.test(configuredApiBase))
      return new URL(configuredApiBase).origin;
  }
  catch {
    // noop
  }
  if (typeof window !== 'undefined' && window.location?.origin)
    return window.location.origin;
  return '';
}

function toAbsoluteResourceUrl(value: unknown) {
  const raw = String(value || '').trim();
  if (!raw)
    return '';
  if (raw.startsWith('<'))
    return raw;
  if (/^(?:https?:)?\/\//i.test(raw) || raw.startsWith('data:') || raw.startsWith('blob:'))
    return upgradeToHttpsIfNeeded(raw);

  const base = configuredStaticBase || resolveApiOrigin();
  if (!base)
    return raw;

  try {
    return upgradeToHttpsIfNeeded(new URL(raw, `${base}/`).toString());
  }
  catch {
    return raw;
  }
}

function normalizeVisualizationItem(item: VisualizationResource): VisualizationResource {
  if (!item || typeof item !== 'object')
    return item;
  return {
    ...item,
    content: toAbsoluteResourceUrl(item.content),
    documentation: toAbsoluteResourceUrl(item.documentation),
  };
}

export interface VisualResponse {
  id: number;
  name: string;
  content: string;
  author: any;
  subject: any;
  chapter: any;
  contributors: string;
  school: string;
  review_status: string;
  review_reason: string;
  likes: number;
}

export interface VisualizationResource {
  id: number;
  name: string;
  name_en?: string;
  description?: string;
  content: string;
  documentation?: string;
  author: number | User | null;
  subject?: {
    id: number;
    name: string;
    description?: string;
  } | null;
  chapter?: {
    id: number;
    name: string;
    description?: string;
  } | null;
  contributors: string;
  school: string;
  review_status: string;
  likes: number;
  views?: number;
  major?: string;
  created_at?: string;
  updated_at?: string;
  display_order?: number;
  can_reorder?: boolean;
  knowledge_points?: Array<{
    id: number;
    name: string;
    description?: string;
  }>;
  [property: string]: any;
}

export interface MyVisual {
  id: number;
  name: string;
  author: User;
  contributors: User[];
  review_status: string;
  review_reason: string;
  likes: number;
  subject?: VisualizationResource['subject'];
  chapter?: VisualizationResource['chapter'];
  updated_at?: string;
  [property: string]: any;
}

export interface User {
  id: number;
  username: string;
  real_name: string;
}

export interface KnowledgePoint {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export type KnowledgePointsResponse = KnowledgePoint[] | { knowledgepoints: KnowledgePoint[] } | { results: KnowledgePoint[] };

function normalizeVisualizationList(resp: any): VisualizationResource[] {
  const payload = resp?.data ?? resp;
  if (Array.isArray(payload))
    return payload.map(normalizeVisualizationItem);
  if (Array.isArray(payload?.results))
    return payload.results.map(normalizeVisualizationItem);
  if (Array.isArray(payload?.data))
    return payload.data.map(normalizeVisualizationItem);
  if (payload && typeof payload === 'object' && payload.id && payload.name)
    return [normalizeVisualizationItem(payload as VisualizationResource)];
  return [];
}

async function fetchVisualizationListApi(params?: Record<string, any>) {
  const result = await getWithFallback<any>('/api/visualizations/', params);
  return normalizeVisualizationList(result.data);
}

async function fetchVisualizationReviewQueueApi(params?: Record<string, any>) {
  const result = await getWithFallback<any>('/api/visualizations/review-queue/', params);
  return normalizeVisualizationList(result.data);
}

async function reviewVisualizationApi(
  visualizationId: number | string,
  reviewStatus: 'approved' | 'rejected',
  reviewReason?: string,
) {
  const result = await post<VisualizationResource>(
    `/api/visualizations/${visualizationId}/review/`,
    {
      review_status: reviewStatus,
      ...(reviewStatus === 'rejected' ? { review_reason: reviewReason || '' } : {}),
    },
  );
  return result.data;
}

async function deleteVisualizationApi(visualizationId: number | string) {
  await del(`/api/visualizations/${visualizationId}/`);
}

async function fetchApprovedVisualizationListApi(params?: Record<string, any>) {
  const list = await fetchVisualizationListApi(params);
  return list.filter(item => String(item.review_status || '').toLowerCase() === 'approved');
}

async function fetchVisualizationDetailApi(visualizationId: number | string) {
  const result = await getWithFallback<any>(`/api/visualizations/${visualizationId}/`);
  const list = normalizeVisualizationList(result.data);
  return list[0] ?? null;
}

async function uploadVisualizationApi(
  name: string,
  author: string,
  subject_id: number,
  chapter_id: number,
  contributors: string,
  school: string,
  knowledge_point_ids: number | number[],
  content?: File | null,
  documentation?: File | null,
) {
  const form = new FormData();
  form.append('name', String(name));
  form.append('author', String(author));
  form.append('subject_id', String(subject_id));
  form.append('chapter_id', String(chapter_id));
  form.append('contributors', String(contributors));
  form.append('school', String(school));
  if (Array.isArray(knowledge_point_ids)) {
    for (const id of knowledge_point_ids) {
      form.append('knowledge_point_ids', String(id));
    }
  }
  else {
    form.append('knowledge_point_ids', String(knowledge_point_ids));
  }
  if (content)
    form.append('content', content);
  if (documentation)
    form.append('documentation', documentation);

  const result = await post<VisualResponse>('/api/visualizations/', form);
  return result.data;
}

async function fetchMyVisualizationApi() {
  const result = await getWithFallback<{
    results: MyVisual[];
  }>('/api/my-review-progress/');
  return result.data;
}

async function resubmitVisualizationApi(visualizationId: number | string) {
  const result = await post<VisualizationResource>(
    `/api/visualizations/${visualizationId}/resubmit/`,
    {},
  );
  return normalizeVisualizationItem(result.data);
}

async function editFileApi(
  visualizationId: number,
  content?: File | string,
  documentation?: File | string,
  clearDocumentation = false,
) {
  const form = new FormData();
  if (content instanceof File) {
    form.append('content', content);
  }
  else if (typeof content === 'string' && content.trim()) {
    form.append('content', content);
  }
  if (documentation instanceof File) {
    form.append('documentation', documentation);
  }
  else if (typeof documentation === 'string' && documentation.trim()) {
    form.append('documentation', documentation);
  }
  if (clearDocumentation) {
    form.append('clear_documentation', 'true');
  }
  const result = await put<{
    content: File;
    documentation: File | null;
    review_status: string;
  }>(`/api/visualizations/${visualizationId}/documentation/`, form);
  return result.data;
}

async function fetchVisualizationApi(visualizationName: string) {
  const list = await fetchVisualizationListApi({ name: visualizationName });
  const normalizedName = String(visualizationName || '').trim();
  const matches = list.filter(item => String(item.name || '').trim() === normalizedName);
  if (matches.length > 0)
    return matches;

  const fallback = await fetchVisualizationListApi();
  return fallback.filter(item => String(item.name || '').trim() === normalizedName);
}

async function reorderVisualizationsApi(
  subjectId: number | string,
  chapterId: number | string,
  visualizationIds: number[],
) {
  const result = await post<{ visualization_ids: number[] }>('/api/visualizations/reorder/', {
    subject_id: subjectId,
    chapter_id: chapterId,
    visualization_ids: visualizationIds,
  });
  return result.data;
}

async function fetchVisualizationMenuOrderApi(subjectId: number | string) {
  const result = await getWithFallback<{ orders: Record<string, string[]> }>('/api/visualizations/menu-order/', {
    subject_id: subjectId,
  });
  return result.data?.orders ?? {};
}

async function saveVisualizationMenuOrderApi(
  subjectId: number | string,
  groupKey: string,
  orderedKeys: string[],
) {
  const result = await post<{ group_key: string; ordered_keys: string[] }>('/api/visualizations/menu-order/', {
    subject_id: subjectId,
    group_key: groupKey,
    ordered_keys: orderedKeys,
  });
  return result.data;
}
async function fetchKnowledgePointsApi(chapterId: number) {
  const result = await getWithFallback<KnowledgePointsResponse>(
    `/api/chapters/${chapterId}/knowledgepoints/`,
  );
  return result.data;
}

async function fetchSubjectKnowledgePointsApi(subjectId: number | string) {
  const result = await getWithFallback<KnowledgePointsResponse>(`/api/knowledgepoints/`, { subject_id: subjectId });
  return result.data;
}
async function putVisualApi(
  visualizationId: number,
  likes: number,
) {
  const result = await put<{
    results: MyVisual[];
  }>(`/api/visualizations/${visualizationId}/set_likes/`, {
    likes,
  });
  return result.data;
}

export {
  deleteVisualizationApi,
  editFileApi,
  fetchApprovedVisualizationListApi,
  fetchKnowledgePointsApi,
  fetchMyVisualizationApi,
  fetchSubjectKnowledgePointsApi,
  fetchVisualizationApi,
  fetchVisualizationDetailApi,
  fetchVisualizationListApi,
  fetchVisualizationMenuOrderApi,
  fetchVisualizationReviewQueueApi,
  putVisualApi,
  reorderVisualizationsApi,
  resubmitVisualizationApi,
  reviewVisualizationApi,
  saveVisualizationMenuOrderApi,
  uploadVisualizationApi,
};
